import { toMajorString } from '@his/shared';
import { humanize, type CsvColumn } from '@/lib/utils';

export const REASON_MIN_LENGTH = 5;

export type StatementKind = 'grn' | 'grn_void' | 'purchase_return' | 'supplier_payment' | 'supplier_payment_reversal';

export type KindVariant = 'secondary' | 'warning' | 'success' | 'destructive' | 'muted';

export interface StatementRow {
  at: string;
  kind: StatementKind;
  refId: string;
  number: string;
  detail: string | null;
  amountMinor: number;
  balanceMinor: number;
  dueDate?: string | null;
}

const KIND_LABEL: Record<StatementKind, string> = {
  grn: 'GRN',
  grn_void: 'GRN voided',
  purchase_return: 'Return',
  supplier_payment: 'Payment',
  supplier_payment_reversal: 'Payment reversed',
};

const KIND_VARIANT: Record<StatementKind, KindVariant> = {
  grn: 'secondary',
  grn_void: 'muted',
  purchase_return: 'warning',
  supplier_payment: 'success',
  supplier_payment_reversal: 'destructive',
};

const CHARGE_KINDS: readonly string[] = ['grn', 'supplier_payment_reversal'];

export function statementKindLabel(kind: string): string {
  return KIND_LABEL[kind as StatementKind] ?? humanize(kind, 'statement_kind');
}

export function statementKindVariant(kind: string): KindVariant {
  return KIND_VARIANT[kind as StatementKind] ?? 'secondary';
}

export function isStatementCharge(kind: string): boolean {
  return CHARGE_KINDS.includes(kind);
}

export function signedStatementAmount(row: Pick<StatementRow, 'kind' | 'amountMinor'>): number {
  return isStatementCharge(row.kind) ? row.amountMinor : -row.amountMinor;
}

export function statementCsvColumns(): CsvColumn<StatementRow>[] {
  return [
    { header: 'Date', value: (row) => row.at },
    { header: 'Type', value: (row) => statementKindLabel(row.kind) },
    { header: 'Document', value: (row) => row.number },
    { header: 'Detail', value: (row) => row.detail },
    { header: 'Due date', value: (row) => row.dueDate },
    { header: 'Charge', value: (row) => (isStatementCharge(row.kind) ? toMajorString(row.amountMinor) : '') },
    { header: 'Credit', value: (row) => (isStatementCharge(row.kind) ? '' : toMajorString(row.amountMinor)) },
    { header: 'Balance', value: (row) => toMajorString(row.balanceMinor) },
  ];
}

export function overdueLabel(daysOverdue: number | null | undefined): string | null {
  if (!daysOverdue || daysOverdue <= 0) return null;
  return `${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} overdue`;
}

export interface PriceVariance {
  stockCostMinor: number;
  priceVarianceMinor: number;
}

export function priceVarianceNote(result: Partial<PriceVariance>, format: (minor: number) => string): string | null {
  const { stockCostMinor, priceVarianceMinor } = result;
  if (stockCostMinor === undefined || priceVarianceMinor === undefined || priceVarianceMinor === 0) return null;
  const creditMinor = stockCostMinor - priceVarianceMinor;
  return (
    `Stock left at cost ${format(stockCostMinor)}; the supplier credit at their invoice price is ${format(creditMinor)}; ` +
    `the difference ${format(Math.abs(priceVarianceMinor))} is booked to Purchase Price Variance.`
  );
}

export function reasonProblem(reason: string): string | null {
  const length = reason.trim().length;
  if (length >= REASON_MIN_LENGTH) return null;
  return `Give a reason of at least ${REASON_MIN_LENGTH} characters.`;
}

export function canReversePayment(status: string, mayApprove: boolean): boolean {
  return mayApprove && status === 'paid';
}

export function hasVoucher(status: string): boolean {
  return status === 'paid' || status === 'reversed';
}
